import React, { useMemo, useState } from 'react';
import { Box, emailValidation, Field, FieldTypes } from '@common-fe/common-fe';
import * as yup from 'yup';

import { REQUIRED_TEXT } from '@/common/constants';
import { useGeneralInfoStore } from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/stores';

import ResendVerificationLink from '../components/ResendVerificationLink';
import VerificationHint from '../components/VerificationHint';

const VALIDATOR = yup.string().trim().nullable();

interface Props {
  isEditMode?: boolean;
}

const useEmployerEmailField = ({ isEditMode }: Props) => {
  const [employerEmailVerificationErrorText, setEmployerEmailVerificationErrorText] = useState<string | undefined>(undefined);
  const defaultFields = useGeneralInfoStore((state) => state.sourceState);
  const isEmployerEmailVerified = useGeneralInfoStore((state) => state.state.isEmployerEmailVerified);
  const employeeEmail = useGeneralInfoStore((state) => state.state.employeeEmail);

  const showResendVerificationLink = useMemo(() => defaultFields?.employerEmail && defaultFields?.employeeId && !isEmployerEmailVerified && !isEditMode,
    [defaultFields, isEmployerEmailVerified, isEditMode]);

  const showVerificationHint = useMemo(() => !!defaultFields?.employerEmail, [defaultFields?.employerEmail]);

  const employerEmailField: Field = useMemo(() => ({
    name: 'employerEmail',
    type: FieldTypes.Text,
    label: 'Employer provided email',
    placeholder: 'Enter email',
    fill: !isEditMode,
    fieldWrapperStyles: {
      flexDirection: 'row',
    },
    ...(showVerificationHint ? {
      rightIcon: <VerificationHint isVerified={isEmployerEmailVerified} />,
    } : {}),
    ...(showResendVerificationLink || showVerificationHint && !isEditMode ? {
      fieldChildren: (
        <Box height="40px" direction="row" align="center" margin={{ left: 'spacing4' }}>
          <VerificationHint isVerified={isEmployerEmailVerified} />
          {showResendVerificationLink && (
            <Box margin={{ left: 'spacing16' }}>
              <ResendVerificationLink
                valueKey={`${defaultFields?.employeeId}_${defaultFields?.employerEmail}`}
                employeeId={defaultFields?.employeeId as string}
                value={defaultFields?.employerEmail as string}
                setVerificationErrorText={setEmployerEmailVerificationErrorText}
              />
            </Box>
          )}
        </Box>
      ),
    } : {}),
    rightIconWrapperProps: { style: { opacity: 1 } },
    disabled: !isEditMode,
    validator: isEditMode
      ? emailValidation.test({
        test: (value) => Boolean(value || employeeEmail),
        message: REQUIRED_TEXT,
      }) : VALIDATOR,
    ...(employerEmailVerificationErrorText ? { manualError: employerEmailVerificationErrorText } : {}),
  }), [defaultFields, employeeEmail, employerEmailVerificationErrorText, isEmployerEmailVerified,
    showVerificationHint, showResendVerificationLink, isEditMode]);

  return employerEmailField;
};

export default useEmployerEmailField;
